import { Dialog, Popover, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import styles from "./list.module.css";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { ChevronDown, ChevronUp, Code, User, X } from "react-feather";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { toast } from "react-smart-toaster";
import { TableLoader } from "../../../../../components/LoaderC";
import { buildFilterParams } from "../../../petQuery";

const INITIAL_LIMIT = 20;
const CHUNK_SIZE = 10;

const List = ({ isActive, query, refreshKey, setEditModalOpen, setEditData }) => {
  const { user, isAuthenticated, getAccessTokenSilently } = useAuth0();
  const redirect = useHistory();
  const [filterredData, setFilterredData] = useState([]);
  const [sortItem, setSortItem] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [carImage, setCarImage] = useState("");
  const [openPop, setOpenPop] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [refetching, setRefetching] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(false);

  const sentinelRef = useRef(null);
  const mobileSentinelRef = useRef(null);
  const requestSeq = useRef(0);
  const hasLoadedOnceRef = useRef(false);
  const loadedKeyRef = useRef(null);

  const handleOpen = () => {
    setOpenPop(true);
  }

  const handleClose = () => {
    setOpenPop(false);
  }

  const changeSortOrder = (type) => {
    if (type === sortItem) {
      if (sortOrder === "asc") {
        setSortOrder("desc");
      } else {
        setSortOrder("asc");
      }
    } else {
      setSortOrder("asc");
    }
    setSortItem(type);
  };

  const fetchPets = useCallback(async (currentOffset, replace) => {
    const seq = ++requestSeq.current;

    let activeIndicator = "loadingMore";
    if (replace && !hasLoadedOnceRef.current) {
      activeIndicator = "contentLoading";
      setContentLoading(true);
    } else if (replace) {
      activeIndicator = "refetching";
      setRefetching(true);
    } else {
      setLoadingMore(true);
    }
    setLoadMoreError(false);

    let params = { ...buildFilterParams(query), offset: currentOffset, limit: currentOffset === 0 ? INITIAL_LIMIT : CHUNK_SIZE };
    if (sortItem !== "") {
      params.sort_by = sortItem;
      params.sort_order = sortOrder;
    }

    try {
      const token = await getAccessTokenSilently({ audience: process.env.REACT_APP_AUTH0_AUDIENCE });

      const response = await axios(process.env.REACT_APP_TENANT_API_URL + "v1/pets/list", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: params,
      });

      if (seq !== requestSeq.current) return;

      const newChunk = response.data?.data || [];
      const meta = response.data?.meta || {};

      setFilterredData((prev) => (replace ? newChunk : prev.concat(newChunk)));
      setOffset(meta?.next_offset ?? currentOffset + newChunk.length);
      setHasMore(Boolean(meta?.has_more));

      setLoadError(false);
      hasLoadedOnceRef.current = true;
    } catch (error) {
      if (seq !== requestSeq.current) return;
      console.error("[Pets] Failed to load pet list", error);
      toast.error(error?.response?.data?.message || "Unable to load pets. Please try again.");

      if (replace) {
        // Allow the next activation of the list view to try again
        loadedKeyRef.current = null;
        setLoadError(true);
        setFilterredData([]);
        setHasMore(false);
      } else {
        setLoadMoreError(true);
      }
    } finally {
      if (seq === requestSeq.current) {
        if (activeIndicator === "contentLoading") setContentLoading(false);
        else if (activeIndicator === "refetching") setRefetching(false);
        else setLoadingMore(false);
      }
    }
  }, [getAccessTokenSilently, query, sortItem, sortOrder]);

  // Load the first chunk only while the list view is visible, and only if search, filters,
  // sorting or data changed since the last load (switching views back doesn't refetch)
  const requestKey = JSON.stringify([query, sortItem, sortOrder, refreshKey]);
  useEffect(() => {
    if (!isActive || loadedKeyRef.current === requestKey) return;
    loadedKeyRef.current = requestKey;
    fetchPets(0, true);
  }, [isActive, requestKey, fetchPets]);

  // Infinite scroll: load the next chunk when the sentinel enters the viewport
  useEffect(() => {
    const desktopNode = sentinelRef.current;
    const mobileNode = mobileSentinelRef.current;
    if (!isActive || (!desktopNode && !mobileNode)) return;

    const observer = new IntersectionObserver((entries) => {
      const isAnyIntersecting = entries.some((entry) => entry.isIntersecting);
      if (isAnyIntersecting && hasMore && !loadingMore && !contentLoading && !refetching && !loadMoreError) {
        fetchPets(offset, false);
      }
    }, { threshold: 0.1 });

    if (desktopNode) observer.observe(desktopNode);
    if (mobileNode) observer.observe(mobileNode);

    return () => observer.disconnect();
  }, [isActive, hasMore, loadingMore, contentLoading, refetching, loadMoreError, offset, fetchPets]);

  const goToDetails = (item) => {
    if (isAuthenticated && user["https://propertydek.com/permissions"].indexOf("pet:update") > -1) {
      setEditModalOpen(true);
      setEditData(item);
    } else {
      return false;
    }
  }

  const handlePopoverOpen = (event) => {
    setAnchorEl(event.currentTarget);
  }

  const handlePopoverClose = () => {
    setAnchorEl(null);
    setCarImage("");
  }

  const open = Boolean(anchorEl);

  const isWithin30Days = (createdAt) => {
    if (!createdAt) return false;
    const createdDate = new Date(createdAt);
    const today = new Date();
    const diffInDays = (today - createdDate) / (1000 * 60 * 60 * 24);
    return diffInDays <= 30;
  };

  if (contentLoading) {
    return (<div className="Loader TableLoader">
      <TableLoader />
    </div>);
  }

  return (<div className={refetching ? `${styles.IncomeListUnderSec} ${styles.Refetching}` : `${styles.IncomeListUnderSec}`}>
    <div className={`${styles.ListCardDivSec}`}>
      {filterredData.map((item) => {
        const showBadge = isWithin30Days(item.created_at);
        return (<div key={item?.id} className={`${styles.CardDivList}`} onClick={(e) => {
          if (isAuthenticated && user["https://propertydek.com/permissions"].indexOf("pet:update") > -1) {
            setEditModalOpen(true);
            setEditData(item);
          } else {
            return false;
          }
        }}>
          <div className={`${styles.FirstSec} ${styles.ProfileImg}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); redirect.push("/people/tenants/details/" + item.tenant_id); }}>
            {item?.tenant_id ? (<>
              {item?.tenant_image !== "" ? (<img src={item?.tenant_image} alt="" />) : (<div className={`${styles.ProNameIMG}`}>
                <p>{item?.tenant_initial}</p>
              </div>)}
            </>) : (<div className={`${styles.ProNameIMG}`}>
              <User />
            </div>)}
            <div>
              <p className={`${styles.Name} ${styles.Hover}`}>{item?.tenant_id ? <>{item?.tenant_name}</> : "-"}</p>
              <p className={`${styles.Name} ${styles.Hover}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); redirect.push("/properties/details/" + item?.property_id); }}>{item.property_name} - {item.apt_no}</p>
            </div>
          </div>
          <div className={`${styles.SecondSec}`}>
            <p className={`${styles.Name}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCarImage(item?.image_path); handleOpen(); }}>
              Pets Name: <span>{item.title}</span>
              {item?.image_path && (<img src={item?.image_path} alt="" className={`${styles.CarImgForSmall}`} />)}
            </p>
            <p className={`${styles.Name}`}>
              Type: <span>{item.type}</span> Weight: <span>{item.weight}kg</span> Color: <span>{item.color}</span>
            </p>
          </div>
          <div className={`${styles.ThirdSec}`}>
            {showBadge && (<span className={styles.NewBadge}>New</span>)}
          </div>
        </div>);
      })}
    </div>

    {filterredData?.length > 0 && (<TableContainer className={`${styles.TableDiv} CustomTableDiv`}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell className={`${styles.TableHead}`} style={{ width: 300 }} onClick={(e) => changeSortOrder("tenant_name")}>
              Tenant
              {sortItem === "tenant_name" && sortOrder === "asc" && (<ChevronDown />)}
              {sortItem === "tenant_name" && sortOrder === "desc" && (<ChevronUp />)}
              {sortItem !== "tenant_name" && (<Code className={`${styles.Normal}`} />)}
            </TableCell>
            <TableCell className={`${styles.TableHead}`} style={{ width: 200 }} onClick={(e) => changeSortOrder("title")}>
              Pets Name
              {sortItem === "title" && sortOrder === "asc" && (<ChevronDown />)}
              {sortItem === "title" && sortOrder === "desc" && (<ChevronUp />)}
              {sortItem !== "title" && (<Code className={`${styles.Normal}`} />)}
            </TableCell>
            <TableCell className={`${styles.TableHead}`} style={{ width: 150 }} onClick={(e) => changeSortOrder("type")}>
              Type
              {sortItem === "type" && sortOrder === "asc" && (<ChevronDown />)}
              {sortItem === "type" && sortOrder === "desc" && <ChevronUp />}
              {sortItem !== "type" && (<Code className={`${styles.Normal}`} />)}
            </TableCell>
            <TableCell className={`${styles.TableHead}`} style={{ width: 150 }} onClick={(e) => changeSortOrder("weight")}>
              Weight
              {sortItem === "weight" && sortOrder === "asc" && (<ChevronDown />)}
              {sortItem === "weight" && sortOrder === "desc" && (<ChevronUp />)}
              {sortItem !== "weight" && (<Code className={`${styles.Normal}`} />)}
            </TableCell>
            <TableCell className={`${styles.TableHead}`} style={{ width: 200 }} onClick={(e) => changeSortOrder("color")}>
              Color
              {sortItem === "color" && sortOrder === "asc" && (<ChevronDown />)}
              {sortItem === "color" && sortOrder === "desc" && (<ChevronUp />)}
              {sortItem !== "color" && (<Code className={`${styles.Normal}`} />)}
            </TableCell>
            <TableCell className={`${styles.TableHead}`} style={{ width: 200 }} onClick={(e) => changeSortOrder("property_name")}>
              Property
              {sortItem === "property_name" && sortOrder === "asc" && (<ChevronDown />)}
              {sortItem === "property_name" && sortOrder === "desc" && (<ChevronUp />)}
              {sortItem !== "property_name" && (<Code className={`${styles.Normal}`} />)}
            </TableCell>
            <TableCell className={`${styles.TableHead}`} style={{ width: 150 }} onClick={(e) => changeSortOrder("apt_no")}>
              Unit
              {sortItem === "apt_no" && sortOrder === "asc" && (<ChevronDown />)}
              {sortItem === "apt_no" && sortOrder === "desc" && (<ChevronUp />)}
              {sortItem !== "apt_no" && (<Code className={`${styles.Normal}`} />)}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filterredData.map((item) => {
            const showBadge = isWithin30Days(item.created_at);
            return (<TableRow key={item?.id} onClick={(e) => goToDetails(item)}>
              <TableCell className={`${styles.TableBodyText} ${styles.ProfileImg} ${styles.Hover}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); redirect.push("/people/tenants/details/" + item.tenant_id); }}>
                {item?.tenant_id ? (<>
                  <div className={`${styles.TenantInside}`}>
                    {item?.tenant_image !== "" ? (<img src={item?.tenant_image} alt="" />) : (<div className={`${styles.ProNameIMG}`}>
                      <p>{item?.tenant_initial}</p>
                    </div>)}
                    <span>{item?.tenant_name}</span>
                    {showBadge && (<span className={styles.NewBadge}>New</span>)}
                  </div>
                </>) : (<div className={`${styles.TenantInside}`}>
                  <div className={`${styles.ProNameIMG}`}>
                    <User />
                  </div>{" "}
                  -
                </div>)}
              </TableCell>
              <TableCell className={`${styles.TableBodyText} ${styles.CarImg}`} onMouseEnter={(e) => { setCarImage(item?.image_path); handlePopoverOpen(e); }} onMouseLeave={handlePopoverClose}>
                {item?.title}
                {item?.image_path && (<img src={item?.image_path} alt="" />)}
              </TableCell>
              <TableCell className={`${styles.TableBodyText}`}>{item?.type}</TableCell>
              <TableCell className={`${styles.TableBodyText}`}>{item?.weight}kg</TableCell>
              <TableCell className={`${styles.TableBodyText}`}>{item?.color}</TableCell>
              <TableCell className={`${styles.TableBodyText} ${styles.Hover}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); redirect.push("/properties/details/" + item?.property_id); }}>
                <span>{item.property_name}</span>
              </TableCell>
              <TableCell className={`${styles.TableBodyText}`}>{item.apt_no}</TableCell>
            </TableRow>);
          })}
        </TableBody>
      </Table>
    </TableContainer>)}

    {/* Sentinel: entering viewport loads next chunk (desktop table and tablet cards) */}
    <div ref={sentinelRef} className={`${styles.ScrollSentinel} ${styles.DesktopSentinel}`} />

    <div className={`${styles.ListMobileView}`}>
      {filterredData.map((item) => {
        const showBadge = isWithin30Days(item.created_at);
        return (<div key={item?.id} className={`${styles.MaintenanceReqCard}`} onClick={(e) => {
          if (isAuthenticated && user["https://propertydek.com/permissions"].indexOf("pet:update") > -1) {
            setEditModalOpen(true);
            setEditData(item);
          } else {
            return false;
          }
        }}>
          <div className={`${styles.CardImgSec} Secondary`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCarImage(item?.image_path); handleOpen(); }}>
            {item?.image_path && (<img src={item?.image_path} alt="" />)}
          </div>
          <div className={`${styles.MainReCardCon}`}>
            <div>
              <p className={`${styles.Title}`}>
                {item?.tenant_id ? (<>
                  <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); redirect.push("/people/tenants/details/" + item.tenant_id); }}>{item?.tenant_name}</span>
                </>) : ('')}
                {item.title}
              </p>
              {parseInt(item?.is_guest) === 1 && (<span className={`${styles.PublishBadge}`}>Guest</span>)}
              <div className={`${styles.MainReCardIdStatus}`}>
                <p className={`${styles.MainReID}`}>{item.type} <span>{item.weight}kg</span><span>{item.color}</span></p>
              </div>
              <div className={`${styles.MainReCardIdStatus}`}>
                <p className={`${styles.MainReID} ${styles.Hover}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); redirect.push("/properties/details/" + item?.property_id); }}>{item.property_name} - {item.apt_no}</p>
                {showBadge && (<span className={styles.NewBadge}>New</span>)}
              </div>
            </div>
          </div>
        </div>);
      })}
      <div ref={mobileSentinelRef} className={`${styles.ScrollSentinel}`} />
    </div>

    {loadingMore && (<div className={`${styles.ScrollStatus}`}>
      <p>Loading more pets...</p>
    </div>)}

    {loadMoreError && (<div className={`${styles.ScrollStatus}`}>
      <p>Couldn't load more pets.</p>
      <button type="button" className={`${styles.RetryBu}`} onClick={() => fetchPets(offset, false)}>Retry</button>
    </div>)}

    {loadError && (<div className={`${styles.NoDataMain}`}>
      <p className={`${styles.NoDataText}`}>Something went wrong!</p>
      <p className={`${styles.NoDataTextSub}`}>we couldn't load the pets.</p>
      <button type="button" className={`${styles.RetryBu}`} onClick={() => { loadedKeyRef.current = requestKey; fetchPets(0, true); }}>Retry</button>
    </div>)}

    {!loadError && filterredData.length === 0 && (<div className={`${styles.NoDataMain}`}>
      <div className={`${styles.NoDataIMG}`}>
        <img src="/static/img/pawprint.png" alt="No Data" />
      </div>
      <p className={`${styles.NoDataText}`}>Unfortunately!</p>
      <p className={`${styles.NoDataTextSub}`}>
        we couldn't find any pet at this time.
      </p>
    </div>)}

    <Popover
      id="mouse-over-popover"
      sx={{ pointerEvents: "none" }}
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
      onClose={handlePopoverClose}
      disableRestoreFocus
    >
      <div className={`${styles.PopupImgSec}`}>
        <img src={carImage} alt="" />
      </div>
    </Popover>

    <Dialog
      open={openPop}
      onClose={(e) => { e.preventDefault(); e.stopPropagation(); handleClose(); }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
    >
      <div className={`${styles.PopupImgSec}`}>
        <img src={carImage} alt="" />
        <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleClose(); }}><X /></button>
      </div>
    </Dialog>
  </div>);
};

export default List;